import React from "react";
import {
  Card,
  Elevation,
  Tag,
  Button,
  H1,
  H3,
  Text,
  InputGroup,
  Intent,
  Icon,
  Tooltip,
  Position,
  NonIdealState,
  Spinner,
  FormGroup,
  ControlGroup,
  Alert,
} from "@blueprintjs/core";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine-dark.css";
import { LicenseManager } from "ag-grid-enterprise";
import "ag-grid-enterprise";
import { inject, observer } from "mobx-react";
import { apiCallPost } from "../../../micro/fns/util";
import cogoToast from "cogo-toast";
import _ from "lodash";
import BranchService from "../../../micro/services/BranchService";
import AuditService from "../../../micro/services/AuditService";
import NewMultiSelector from "../../../micro/components/NewMultiSelector";
import Modal from "../../../micro/components/Modal";
import AuditLogViewer from "../../../micro/components/AuditLogViewer";

// Set AG Grid license
LicenseManager.setLicenseKey("");

class InsurancePanelMaster extends React.Component {
  state = {
    // Data
    panels: [],
    branches: [],

    // Loading states
    isLoading: true,
    isSaving: false,

    // Grid state
    gridApi: null,
    columnApi: null,
    quickFilterText: "",

    // Form state for adding new panel
    newPanel: {
      panelName: "",
      branches: [],
    },

    // UI state for new panel modal
    showNewPanelModal: false,

    // Edit modal state
    isEditModalOpen: false,
    editingPanel: null,

    // Audit log state
    showAuditLogs: false,

    // Delete confirmation state
    showDeleteConfirmation: false,
    panelToDelete: null,
  };

  componentDidMount() {
    this.loadData();
  }

  onGridReady = (params) => {
    this.setState({ gridApi: params.api, columnApi: params.columnApi });
  };

  columnDefs = [
    {
      headerName: "Panel Name",
      field: "panelName",
      width: 200,
      filter: "agTextColumnFilter",
      pinned: "left",
      editable: false,
    },
    {
      headerName: "Branches",
      field: "branches",
      width: 300,
      filter: "agTextColumnFilter",
      cellRenderer: (params) => {
        if (!params.value || params.value.length === 0) {
          return "No branches";
        }

        if (params.value.length <= 2) {
          return params.value.join(", ");
        }

        return params.value.slice(0, 2).join(", ") + ` (+${params.value.length - 2} more)`;
      },
      editable: false,
    },
    {
      headerName: "Actions",
      field: "actions",
      width: 120,
      cellRenderer: (params) => {
        const container = document.createElement("div");
        const editBtn = document.createElement("button");
        const deleteBtn = document.createElement("button");

        editBtn.innerHTML = "✏️";
        editBtn.style.marginRight = "5px";
        editBtn.style.padding = "4px 8px";
        editBtn.style.border = "1px solid #137cbd";
        editBtn.style.backgroundColor = "#137cbd";
        editBtn.style.color = "white";
        editBtn.style.borderRadius = "3px";
        editBtn.style.cursor = "pointer";
        editBtn.onclick = () => this.editPanel(params.data);

        deleteBtn.innerHTML = "🗑️";
        deleteBtn.style.padding = "4px 8px";
        deleteBtn.style.border = "1px solid #db3737";
        deleteBtn.style.backgroundColor = "#db3737";
        deleteBtn.style.color = "white";
        deleteBtn.style.borderRadius = "3px";
        deleteBtn.style.cursor = "pointer";
        deleteBtn.onclick = () => this.showDeleteConfirmation(params.data);

        container.appendChild(editBtn);
        container.appendChild(deleteBtn);

        return container;
      },
      filter: false,
      sortable: false,
    },
  ];

  loadData = async () => {
    try {
      this.setState({ isLoading: true });
      cogoToast.loading("Loading insurance panel data...");

      // Load panels and branches in parallel
      await Promise.all([this.loadPanels(), this.loadBranches()]);

      cogoToast.success("Data loaded successfully");
    } catch (err) {
      cogoToast.error("Failed to load data. Please try again.");
      console.error("Detailed error loading data:", err);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  loadBranches = async () => {
    try {
      const branchNames = await BranchService.getBranchNames(this.props.authStore.prefix);
      this.setState({ branches: branchNames });
      console.log("✅ DEBUG: Branches loaded successfully:", branchNames.length);

      // Reload panels with updated branches if panels are already loaded
      if (this.state.panels.length > 0) {
        this.initializeStaticPanels();
      }
    } catch (err) {
      console.error("Failed to load branches:", err);
      // Fallback to dummy branches
      this.setState({
        branches: ["Newtown Branch", "Park Street Branch", "Salt Lake Branch", "Howrah Branch"],
      });
      console.warn("⚠️ DEBUG: Using fallback branches due to API error");

      // Reload panels with fallback branches if panels are already loaded
      if (this.state.panels.length > 0) {
        this.initializeStaticPanels();
      }
    }
  };

  loadPanels = async () => {
    try {
      console.log("🔍 DEBUG: Loading insurance panels from API");
      const response = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxInsurancePanelMaster/getAll`, {});
      this.setState({ panels: response || [] });
      console.log("✅ DEBUG: Insurance panels loaded successfully:", (response || []).length);
    } catch (err) {
      console.error("Failed to load insurance panels:", err);
      // Fallback to static data if API fails
      // this.initializeStaticPanels();
      console.warn("⚠️ DEBUG: Using static data due to API error");
    }
  };

  initializeStaticPanels = () => {
    const allBranches =
      this.state.branches.length > 0
        ? this.state.branches
        : ["Newtown Branch", "Park Street Branch", "Salt Lake Branch", "Howrah Branch"];

    const staticPanels = [
      {
        _id: "1",
        panelName: "ICICI",
        branches: [...allBranches],
        createdAt: new Date(),
        updatedAt: new Date(),
        isShell: false,
      },
      {
        _id: "2",
        panelName: "Bajaj Tariff",
        branches: [...allBranches],
        createdAt: new Date(),
        updatedAt: new Date(),
        isShell: false,
      },
      {
        _id: "3",
        panelName: "PPN",
        branches: [...allBranches],
        createdAt: new Date(),
        updatedAt: new Date(),
        isShell: false,
      },
      {
        _id: "4",
        panelName: "ONGC",
        branches: [...allBranches],
        createdAt: new Date(),
        updatedAt: new Date(),
        isShell: false,
      },
      {
        _id: "5",
        panelName: "SAIL",
        branches: [...allBranches],
        createdAt: new Date(),
        updatedAt: new Date(),
        isShell: false,
      },
      {
        _id: "6",
        panelName: "CGHS General (Non NABH)",
        branches: [...allBranches],
        createdAt: new Date(),
        updatedAt: new Date(),
        isShell: false,
      },
      {
        _id: "7",
        panelName: "CGHS Semi Private (Non NABH)",
        branches: [...allBranches],
        createdAt: new Date(),
        updatedAt: new Date(),
        isShell: false,
      },
      {
        _id: "8",
        panelName: "CGHS Private (NABH)",
        branches: [...allBranches],
        createdAt: new Date(),
        updatedAt: new Date(),
        isShell: false,
      },
      {
        _id: "9",
        panelName: "ECHS General",
        branches: [...allBranches],
        createdAt: new Date(),
        updatedAt: new Date(),
        isShell: false,
      },
      {
        _id: "10",
        panelName: "ECHS Semi Private",
        branches: [...allBranches],
        createdAt: new Date(),
        updatedAt: new Date(),
        isShell: false,
      },
      {
        _id: "11",
        panelName: "ECHS Private",
        branches: [...allBranches],
        createdAt: new Date(),
        updatedAt: new Date(),
        isShell: false,
      },
      {
        _id: "12",
        panelName: "CESC",
        branches: [...allBranches],
        createdAt: new Date(),
        updatedAt: new Date(),
        isShell: false,
      },
      {
        _id: "13",
        panelName: "Swastha Swathi",
        branches: [...allBranches],
        createdAt: new Date(),
        updatedAt: new Date(),
        isShell: false,
      },
      {
        _id: "14",
        panelName: "BHEL",
        branches: [...allBranches],
        createdAt: new Date(),
        updatedAt: new Date(),
        isShell: false,
      },
      {
        _id: "15",
        panelName: "Cash",
        branches: [...allBranches],
        createdAt: new Date(),
        updatedAt: new Date(),
        isShell: false,
      },
      {
        _id: "16",
        panelName: "Non PPN",
        branches: [...allBranches],
        createdAt: new Date(),
        updatedAt: new Date(),
        isShell: false,
      },
      {
        _id: "17",
        panelName: "Bajaj",
        branches: [...allBranches],
        createdAt: new Date(),
        updatedAt: new Date(),
        isShell: false,
      },
      {
        _id: "18",
        panelName: "CGHS General",
        branches: [...allBranches],
        createdAt: new Date(),
        updatedAt: new Date(),
        isShell: false,
      },
      {
        _id: "19",
        panelName: "CGHS Semi Private",
        branches: [...allBranches],
        createdAt: new Date(),
        updatedAt: new Date(),
        isShell: false,
      },
    ];

    this.setState({ panels: staticPanels });
  };

  editPanel = (panel) => {
    // Open edit modal with prefilled values
    this.setState({
      isEditModalOpen: true,
      editingPanel: { ...panel },
    });
  };

  updatePanelFromModal = async () => {
    const { editingPanel } = this.state;

    // Validation
    if (!editingPanel.panelName.trim()) {
      cogoToast.error("Panel Name is required");
      return;
    }

    if (!editingPanel.branches.length) {
      cogoToast.error("At least one branch must be selected");
      return;
    }

    // Check for duplicate panel names (case-sensitive, including whitespace and special characters)
    const isDuplicate = this.state.panels.some((existingPanel) => {
      // Skip the current panel being edited
      if (existingPanel._id === editingPanel._id) {
        return false;
      }
      // Compare exact names including case, whitespace, and special characters
      return existingPanel.panelName === editingPanel.panelName.trim();
    });

    if (isDuplicate) {
      cogoToast.error(`Panel name "${editingPanel.panelName.trim()}" already exists. Please choose a different name.`);
      return;
    }

    try {
      cogoToast.loading("Updating insurance panel...");
      this.setState({ isSaving: true });

      const updatePayload = {
        _id: editingPanel._id,
        panelName: editingPanel.panelName.trim(),
        branches: editingPanel.branches,
        isShell: false,
      };

      const response = await apiCallPost(
        `/v3/${this.props.authStore.prefix}/neoSxInsurancePanelMaster/update`,
        updatePayload
      );

      if (response && response._id) {
        const updatedPanel = {
          ...response,
          isShell: false,
        };

        this.setState((prevState) => ({
          panels: (prevState.panels || []).map((panel) => (panel._id === editingPanel._id ? updatedPanel : panel)),
          isSaving: false,
          isEditModalOpen: false,
          editingPanel: null,
        }));

        // Log audit event for panel update
        try {
          const auditResult = await AuditService.logEntityUpdate({
            entityType: "insurancePanel",
            entityId: editingPanel._id,
            oldData: this.state.panels.find((p) => p._id === editingPanel._id),
            newData: updatedPanel,
            authStore: this.props.authStore,
            description: `Updated insurance panel "${updatedPanel.panelName}"`,
          });
          console.log("✅ DEBUG: Audit log updated successfully", auditResult);
        } catch (auditError) {
          console.error("❌ DEBUG: Failed to create audit log for update", auditError);
        }

        cogoToast.success("Insurance panel updated successfully!");
      } else {
        throw new Error("Invalid response from update API");
      }
    } catch (err) {
      console.error("❌ DEBUG: Error updating insurance panel:", err);
      cogoToast.error("Failed to update insurance panel. Please try again.");
      this.setState({ isSaving: false });
    }
  };

  closeEditModal = () => {
    this.setState({
      isEditModalOpen: false,
      editingPanel: null,
    });
  };

  handleNewPanelSubmit = () => {
    const { newPanel } = this.state;

    if (!newPanel.panelName.trim()) {
      cogoToast.error("Please enter a panel name");
      return;
    }

    if (!newPanel.branches.length) {
      cogoToast.error("Please select at least one branch");
      return;
    }

    this.setState(
      {
        newPanel: {
          ...newPanel,
        },
      },
      () => {
        this.createNew();
      }
    );
  };

  createNew = async () => {
    this.setState({ isSaving: true });
    cogoToast.loading("Creating new Payment Panel Master");

    try {
      console.log("🔍 DEBUG: Creating new insurance panel via API");

      // Create new panel via API
      const newPanelData = {
        panelName: this.state.newPanel.panelName,
        branches: this.state.newPanel.branches,
        isShell: false,
      };

      const response = await apiCallPost(
        `/v3/${this.props.authStore.prefix}/neoSxInsurancePanelMaster/createNew`,
        newPanelData
      );

      if (response && response._id) {
        const newPanel = {
          ...response,
          isShell: false,
        };

        // Log audit event for panel creation
        console.log("🔍 DEBUG: Creating audit log for insurance panel creation", {
          entityType: "insurancePanel",
          entityId: newPanel._id,
          authStore: this.props.authStore,
          description: `Created new insurance panel "${newPanel.panelName || "Unnamed Panel"}"`,
        });

        try {
          const auditResult = await AuditService.logEntityCreate({
            entityType: "insurancePanel",
            entityId: newPanel._id,
            newData: newPanel,
            authStore: this.props.authStore,
            description: `Created new insurance panel "${newPanel.panelName || "Unnamed Panel"}"`,
          });
          console.log("✅ DEBUG: Audit log created successfully", auditResult);
        } catch (auditError) {
          console.error("❌ DEBUG: Failed to create audit log", auditError);
        }

        this.setState({
          panels: [newPanel, ...this.state.panels],
          isSaving: false,
          showNewPanelModal: false,
          newPanel: {
            panelName: "",
            branches: [],
          },
        });
        cogoToast.success("Insurance panel created successfully! You can now edit it inline.");
      } else {
        throw new Error("Invalid response from create API");
      }
    } catch (err) {
      console.error("❌ DEBUG: Error creating insurance panel:", err);
      cogoToast.error("Failed to create new insurance panel. Please try again.");
      this.setState({ isSaving: false });
    }
  };

  updatePanel = async (_id, updatedData) => {
    this.setState({ isSaving: true });
    cogoToast.loading("Updating insurance panel...");

    // Find the current panel data for audit logging
    const currentPanel = this.state.panels.find((panel) => panel._id === _id);
    const oldData = currentPanel ? { ...currentPanel } : null;

    try {
      console.log("🔍 DEBUG: Updating insurance panel via API", {
        _id,
        updatedData,
      });

      // Prepare update payload
      const updatePayload = {
        _id,
        ...updatedData,
        isShell: false,
      };

      const response = await apiCallPost(
        `/v3/${this.props.authStore.prefix}/neoSxInsurancePanelMaster/update`,
        updatePayload
      );

      if (response && response._id) {
        const updatedPanel = {
          ...response,
          isShell: false,
        };

        this.setState((prevState) => ({
          panels: (prevState.panels || []).map((panel) => (panel._id === _id ? updatedPanel : panel)),
          isSaving: false,
        }));

        // Log audit event for panel update
        console.log("🔍 DEBUG: Creating audit log for insurance panel update", {
          entityType: "insurancePanel",
          entityId: _id,
          oldData: oldData,
          newData: updatedPanel,
          authStore: this.props.authStore,
          description: `Updated insurance panel "${updatedPanel.panelName}"`,
        });

        try {
          const auditResult = await AuditService.logEntityUpdate({
            entityType: "insurancePanel",
            entityId: _id,
            oldData: oldData,
            newData: updatedPanel,
            authStore: this.props.authStore,
            description: `Updated insurance panel "${updatedPanel.panelName}"`,
          });
          console.log("✅ DEBUG: Audit log updated successfully", auditResult);
        } catch (auditError) {
          console.error("❌ DEBUG: Failed to create audit log for update", auditError);
        }

        cogoToast.success("Insurance panel updated successfully!");
      } else {
        throw new Error("Invalid response from update API");
      }
    } catch (err) {
      console.error("❌ DEBUG: Error updating insurance panel:", err);
      cogoToast.error("Failed to update insurance panel. Please try again.");
      this.setState({ isSaving: false });
    }
  };

  showDeleteConfirmation = (panel) => {
    this.setState({
      showDeleteConfirmation: true,
      panelToDelete: panel,
    });
  };

  hideDeleteConfirmation = () => {
    this.setState({
      showDeleteConfirmation: false,
      panelToDelete: null,
    });
  };

  confirmDelete = () => {
    if (this.state.panelToDelete) {
      this.deletePanel(this.state.panelToDelete._id);
      this.hideDeleteConfirmation();
    }
  };

  deletePanel = async (_id) => {
    this.setState({ isSaving: true });
    cogoToast.loading("Deleting insurance panel...");

    // Find the panel data for audit logging
    const panelToDelete = this.state.panels.find((panel) => panel._id === _id);

    try {
      console.log("🔍 DEBUG: Deleting insurance panel via API", {
        _id,
      });

      const response = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxInsurancePanelMaster/delete`, {
        _id,
      });

      if (response && response.success !== false) {
        this.setState((prevState) => ({
          panels: (prevState.panels || []).filter((panel) => panel._id !== _id),
          isSaving: false,
        }));

        // Log audit event for panel deletion
        if (panelToDelete) {
          console.log("🔍 DEBUG: Creating audit log for insurance panel deletion", {
            entityType: "insurancePanel",
            entityId: _id,
            deletedData: panelToDelete,
            authStore: this.props.authStore,
            description: `Deleted insurance panel "${panelToDelete.panelName}"`,
          });

          try {
            const auditResult = await AuditService.logEntityDelete({
              entityType: "insurancePanel",
              entityId: _id,
              deletedData: panelToDelete,
              authStore: this.props.authStore,
              description: `Deleted insurance panel "${panelToDelete.panelName}"`,
            });
            console.log("✅ DEBUG: Audit log deleted successfully", auditResult);
          } catch (auditError) {
            console.error("❌ DEBUG: Failed to create audit log for deletion", auditError);
          }
        }

        cogoToast.success("Insurance panel deleted successfully!");
      } else {
        throw new Error("Delete operation failed or was rejected");
      }
    } catch (err) {
      console.error("❌ DEBUG: Error deleting insurance panel:", err);
      cogoToast.error("Failed to delete insurance panel. Please try again.");
      this.setState({ isSaving: false });
    }
  };

  logAudit = async (action, entityId, description, changes = {}) => {
    try {
      console.log("🔍 DEBUG: Creating audit log", {
        action,
        entityId,
        description,
        changes,
      });

      await AuditService.createAuditLog(
        this.props.authStore.prefix,
        "insurancePanel",
        entityId,
        action,
        description,
        changes
      );

      console.log("✅ DEBUG: Audit log created successfully");
    } catch (error) {
      console.error("❌ DEBUG: Failed to create audit log:", error);
    }
  };

  handleShowAuditLogs = () => {
    this.setState({ showAuditLogs: true });
  };

  handleCloseAuditLogs = () => {
    this.setState({ showAuditLogs: false });
  };

  renderHeader = () => {
    const { quickFilterText, isSaving, panels, branches } = this.state;

    return (
      <div style={{ marginBottom: "var(--v3-spacing-lg)" }}>
        <H1 style={{ color: "var(--text-primary)", margin: 0 }}>Payment Panel Master</H1>
        <Text
          style={{
            color: "var(--text-secondary)",
            margin: "var(--v3-spacing-xs) 0",
            fontSize: "var(--v3-font-size-md)",
            fontFamily: "var(--v3-font-family-sans)",
          }}
        >
          Manage insurance panels and their associated branch networks
        </Text>

        <Card elevation={Elevation.TWO} style={{ padding: "var(--v3-spacing-lg)", marginTop: "var(--v3-spacing-lg)" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "var(--v3-spacing-md)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "16px", flex: 1 }}>
              <div style={{ minWidth: "300px" }}>
                <InputGroup
                  leftIcon="search"
                  placeholder="Search panels or branches..."
                  value={quickFilterText}
                  onChange={(e) => this.setState({ quickFilterText: e.target.value })}
                  disabled={isSaving}
                />
              </div>

              <div style={{ display: "flex", gap: "8px" }}>
                <Tag large intent="primary" icon="numerical">
                  {panels.length} Panels
                </Tag>
                <Tag large intent="success" icon="map-marker">
                  {branches.length} Branches
                </Tag>
              </div>
            </div>

            <div style={{ display: "flex", gap: "8px" }}>
              <Tooltip content="Refresh data" position={Position.BOTTOM}>
                <Button
                  icon="refresh"
                  disabled={isSaving}
                  onClick={() => {
                    this.loadBranches();
                    this.loadPanels();
                  }}
                />
              </Tooltip>

              <Tooltip content="View audit logs" position={Position.BOTTOM}>
                <Button icon="history" disabled={isSaving} onClick={this.handleShowAuditLogs} />
              </Tooltip>

              <Button
                icon="plus"
                intent="primary"
                disabled={isSaving}
                onClick={() => this.setState({ showNewPanelModal: true })}
              >
                New Insurance Panel
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  };

  renderTable = () => {
    const { panels, quickFilterText, isLoading } = this.state;

    if (!panels.length) {
      return (
        <Card elevation={Elevation.TWO} style={{ padding: "20px" }}>
          <div style={{ textAlign: "center", padding: "40px" }}>
            <H3 style={{ color: "var(--text-primary)", marginBottom: "20px" }}>
              {quickFilterText ? "No matching panels found" : "No panels configured"}
            </H3>
            <Text
              style={{
                color: "var(--text-secondary)",
                marginBottom: "var(--v3-spacing-xl)",
                fontSize: "var(--v3-font-size-md)",
                fontFamily: "var(--v3-font-family-sans)",
              }}
            >
              {quickFilterText
                ? "Try adjusting your search terms or clear the search to view all panels."
                : "Get started by creating your first payment panel master."}
            </Text>
            {quickFilterText ? (
              <Button icon="cross" intent="primary" onClick={() => this.setState({ quickFilterText: "" })}>
                Clear Search
              </Button>
            ) : (
              <Button
                icon="plus"
                intent="primary"
                large
                onClick={() => this.setState({ showNewPanelModal: true })}
                loading={this.state.isSaving}
              >
                Create First Panel
              </Button>
            )}
          </div>
        </Card>
      );
    }

    return (
      <Card elevation={Elevation.TWO} style={{ padding: "20px" }}>
        <div className="ag-theme-alpine-dark" style={{ height: "500px", width: "100%" }}>
          <AgGridReact
            columnDefs={this.columnDefs}
            rowData={panels}
            onGridReady={this.onGridReady}
            quickFilterText={quickFilterText}
            defaultColDef={{
              sortable: true,
              filter: true,
              resizable: true,
            }}
            rowSelection="single"
            enableRangeSelection={true}
            enableCharts={true}
            sideBar={true}
            statusBar={{
              statusPanels: [
                {
                  statusPanel: "agTotalAndFilteredRowCountComponent",
                  align: "left",
                },
              ],
            }}
            loading={isLoading}
          />
        </div>
      </Card>
    );
  };

  render() {
    if (this.state.showAuditLogs) {
      return <AuditLogViewer onClose={this.handleCloseAuditLogs} />;
    }

    if (this.state.isLoading) {
      return (
        <div
          className="bp3-dark"
          style={{
            padding: "20px",
            backgroundColor: "var(--bg-primary)",
            color: "var(--text-primary)",
            minHeight: "100vh",
          }}
        >
          <NonIdealState
            icon={<Spinner size={50} />}
            title="Loading Payment Panel Masters"
            description="Please wait while we load your insurance panel data and branch configurations..."
          />
        </div>
      );
    }

    return (
      <div
        className="bp3-dark"
        style={{
          padding: "20px",
          backgroundColor: "var(--bg-primary)",
          color: "var(--text-primary)",
          minHeight: "100vh",
        }}
      >
        {this.renderHeader()}
        {this.renderTable()}
        {this.renderNewPanelModal()}
        {this.renderEditModal()}
        {this.renderDeleteConfirmation()}
      </div>
    );
  }

  renderEditModal = () => {
    const { isEditModalOpen, editingPanel, branches, isSaving } = this.state;

    if (!isEditModalOpen || !editingPanel) return null;

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}
        onClick={this.closeEditModal}
      >
        <div
          style={{
            backgroundColor: "var(--bg-surface)",
            borderRadius: "8px",
            padding: "30px",
            maxWidth: "800px",
            width: "90%",
            maxHeight: "90vh",
            overflowY: "auto",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ marginBottom: "20px" }}>
            <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Edit Insurance Panel</H3>
            <p style={{ color: "var(--text-secondary)", margin: "5px 0" }}>
              Update the insurance panel name and branch assignments.
            </p>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "15px", marginBottom: "20px" }}>
            <ControlGroup>
              <Tag large minimal>
                Panel Name *
              </Tag>
              <InputGroup
                placeholder="Enter insurance panel name..."
                value={editingPanel.panelName}
                onChange={(e) =>
                  this.setState({
                    editingPanel: { ...editingPanel, panelName: e.target.value },
                  })
                }
                disabled={isSaving}
                intent={!editingPanel.panelName.trim() ? Intent.DANGER : Intent.NONE}
                style={{ width: "300px" }}
              />
            </ControlGroup>

            <div style={{ width: "100%", marginTop: "10px" }}>
              <ControlGroup>
                <Tag large minimal>
                  Branches *
                </Tag>
                <div style={{ flex: 1, minWidth: "400px" }}>
                  <NewMultiSelector
                    items={branches}
                    selectedItems={editingPanel.branches}
                    onChange={(selectedBranches) =>
                      this.setState({
                        editingPanel: { ...editingPanel, branches: selectedBranches },
                      })
                    }
                    placeholder="Select branches for this panel..."
                    label="Branches"
                    showLabel={false}
                    maxDisplayItems={3}
                    sortItems={true}
                    emptyMessage="No branches available"
                    disabled={isSaving}
                  />
                </div>
              </ControlGroup>
            </div>
          </div>

          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <Button large intent="none" icon="cross" onClick={this.closeEditModal}>
              Cancel
            </Button>
            <Button
              large
              intent="primary"
              icon="tick"
              onClick={this.updatePanelFromModal}
              loading={isSaving}
              disabled={isSaving || !editingPanel.panelName.trim() || !editingPanel.branches.length}
            >
              Update Panel
            </Button>
          </div>
        </div>
      </div>
    );
  };

  renderNewPanelModal = () => {
    const { showNewPanelModal, newPanel, branches, isSaving } = this.state;

    if (!showNewPanelModal) return null;

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}
        onClick={() =>
          this.setState({
            showNewPanelModal: false,
            newPanel: { panelName: "", branches: [] },
          })
        }
      >
        <div
          style={{
            backgroundColor: "var(--bg-surface)",
            borderRadius: "8px",
            padding: "30px",
            maxWidth: "800px",
            width: "90%",
            maxHeight: "90vh",
            overflowY: "auto",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ marginBottom: "20px" }}>
            <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Create New Insurance Panel</H3>
            <p style={{ color: "var(--text-secondary)", margin: "5px 0" }}>
              Enter the insurance panel name and select the branches where this panel will be available.
            </p>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "15px", marginBottom: "20px" }}>
            <ControlGroup>
              <Tag large minimal>
                Panel Name *
              </Tag>
              <InputGroup
                placeholder="Enter insurance panel name..."
                value={newPanel.panelName}
                onChange={(e) =>
                  this.setState({
                    newPanel: { ...newPanel, panelName: e.target.value },
                  })
                }
                disabled={isSaving}
                intent={!newPanel.panelName.trim() ? Intent.DANGER : Intent.NONE}
                style={{ width: "300px" }}
              />
            </ControlGroup>

            <div style={{ width: "100%", marginTop: "10px" }}>
              <ControlGroup>
                <Tag large minimal>
                  Branches *
                </Tag>
                <div style={{ flex: 1, minWidth: "400px" }}>
                  <NewMultiSelector
                    items={branches}
                    selectedItems={newPanel.branches}
                    onChange={(selectedBranches) =>
                      this.setState({
                        newPanel: { ...newPanel, branches: selectedBranches },
                      })
                    }
                    placeholder="Select branches for this panel..."
                    label="Branches"
                    showLabel={false}
                    maxDisplayItems={3}
                    sortItems={true}
                    emptyMessage="No branches available"
                    disabled={isSaving}
                  />
                </div>
              </ControlGroup>
            </div>
          </div>

          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <Button
              large
              intent="none"
              icon="cross"
              onClick={() =>
                this.setState({
                  showNewPanelModal: false,
                  newPanel: { panelName: "", branches: [] },
                })
              }
            >
              Cancel
            </Button>
            <Button
              large
              intent="primary"
              icon="tick"
              onClick={this.handleNewPanelSubmit}
              loading={isSaving}
              disabled={isSaving || !newPanel.panelName.trim() || !newPanel.branches.length}
            >
              Create Panel
            </Button>
          </div>
        </div>
      </div>
    );
  };

  renderDeleteConfirmation = () => {
    const { showDeleteConfirmation, panelToDelete } = this.state;

    return (
      <Alert
        isOpen={showDeleteConfirmation}
        intent={Intent.DANGER}
        icon="trash"
        onCancel={this.hideDeleteConfirmation}
        onConfirm={this.confirmDelete}
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
      >
        <p>
          Are you sure you want to delete the insurance panel{" "}
          <strong>"{panelToDelete?.panelName}"</strong>?
        </p>
        <p>
          This action cannot be undone and will permanently remove this insurance panel
          from the system.
        </p>
        {panelToDelete?.branches && panelToDelete.branches.length > 0 && (
          <p style={{ marginTop: "10px", fontSize: "14px", color: "var(--text-secondary)" }}>
            <strong>Associated branches:</strong> {panelToDelete.branches.join(", ")}
          </p>
        )}
      </Alert>
    );
  };
}

export default inject("authStore")(observer(InsurancePanelMaster));
